import {
  Box,
  Button,
  LinearProgress,
  MobileStepper,
  Typography,
  TextField,
  MenuItem,
  useTheme,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  InputAdornment,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  FormHelperText,
  Autocomplete,
  Chip,
  Checkbox,
  CircularProgress,
} from "@mui/material";
import React, { useEffect, useState, useMemo } from "react";
import { useForm, Controller, useFieldArray } from "react-hook-form";
import KeyboardArrowRightRoundedIcon from "@mui/icons-material/KeyboardArrowRightRounded";
import KeyboardArrowLeftRoundedIcon from "@mui/icons-material/KeyboardArrowLeftRounded";
import DeleteRoundedIcon from "@mui/icons-material/DeleteRounded";
import { FileUploader } from "react-drag-drop-files";
import { toast } from "react-toastify";
import AddPhotoAlternateRoundedIcon from "@mui/icons-material/AddPhotoAlternateRounded";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs from "dayjs";
import HomeRoundedIcon from "@mui/icons-material/HomeRounded";
import { useDispatch, useSelector } from "react-redux";
import { getPracticeArea } from "../../../redux/Apps/Lawyerapp/CaseSlice/CaseSlice";
import { backend_api, backend_url } from "../../../static/server";
import { CountryCodeSelect } from "../../../styles/styles";
import { genders } from "../../../static/staticdata";
import { AddCircleOutlineRounded, CloseRounded } from "@mui/icons-material";
import axios from "axios";
import { MAX_FILE_SIZE_MB } from "../../../components/LawyerCompo/Contacts/Contact Forms/PersonContactForm";
import { useDialog } from "../../../components/DialogContext";

export const FILE_TYPES = [
  "JPEG",
  "PNG",
  "GIF",
  "BMP",
  "ICO",
  "SVG",
  "JPG",
  "TIFF",
  "WEBP",
];

const steps = [
  { label: "Personal Details" },
  { label: "Education & Certifications" },
  { label: "Professional Details" },
  { label: "Expertise & Preference" },
];

// Education options
const degreeOptions = [
  { id: "LLB", name: "LLB" },
  { id: "LLM", name: "LLM" },
  { id: "JD", name: "Juris Doctor" },
  { id: "SJD", name: "SJD (Doctor of Juridical Science)" },
  { id: "BA_Law", name: "BA in Law" },
  { id: "BSc_Law", name: "BSc in Law" },
];

const certificationOptions = [
  { id: "mediation", name: "Mediation Certification" },
  { id: "arbitration", name: "Arbitration Certification" },
  { id: "tax_law", name: "Tax Law Certification" },
  { id: "ip_law", name: "Intellectual Property Law" },
  { id: "family_law", name: "Family Law Specialization" },
  { id: "criminal_law", name: "Criminal Law Certification" },
  { id: "corporate_law", name: "Corporate Law Certification" },
  { id: "international_law", name: "International Law Certification" },
];

const courtOptions = [
  { id: "local", name: "Local Court" },
  { id: "state", name: "State Court" },
  { id: "federal", name: "Federal Court" },
  { id: "supreme", name: "Supreme Court" },
  { id: "appellate", name: "Appellate Court" },
  { id: "bankruptcy", name: "Bankruptcy Court" },
  { id: "tax_court", name: "Tax Court" },
];

const LawyerProfileForm = ({ onClose }) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const { closeDialog } = useDialog();
  const [activeStep, setActiveStep] = useState(0);
  const [profileImage, setProfileImage] = useState(null);
  const [selectedCountry, setSelectedCountry] = useState("+1");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { token, user } = useSelector((data) => data.user);
  const { allPracticeArea } = useSelector((state) => state.case);
  const { LoginUserData, loading, firmData } = useSelector(
    (state) => state.dashboard,
  );
  const [isImageLoading, setIsImageLoading] = useState(false);

  const progress = Math.min(
    100,
    Math.round(((activeStep + 1) / steps.length) * 100),
  );

  useEffect(() => {
    dispatch(
      getPracticeArea({
        url: `${backend_api}case/case-type/?case_service_type=practice_area`,
        token: token,
      }),
    );
  }, [dispatch, token]);

  const {
    control,
    handleSubmit,
    setValue,
    watch,
    reset,
    formState: { errors },
  } = useForm({
    mode: "onChange",
    defaultValues: {
      first_name: "",
      last_name: "",
      mobile: "",
      address_1: "",
      city: "",
      state: "",
      country: "",
      pincode: "",
      birth_date: null,
      gender: "",
      avatar: null,

      // Professional fields that will go inside extra
      experience: "",
      licenseNumber: "",
      specialization: "",
      consultationFee: "",
      education: [
        {
          universityName: "",
          lawDegree: "",
          graduationYear: "",
          additionalCertifications: [],
        },
      ],
      lawFirmOrIndependent: "",
      previousLawFirm: "",
      positionHeld: "",
      barCouncilRegistrationNumber: "",
      yearsOfExperience: "",
      barLicenseStatus: "",
      jurisdiction: "",
      practice_area: [],
      courtAffiliations: [],
      typesOfCasesHandled: "",
      location: "",
      languages: "",
      salary: "",
      billingPreference: "",
      employmentType: "",
      courtAppearances: false,
      travelWillingness: false,
    },
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: "education",
  });

  const lawFirmOrIndependent = watch("lawFirmOrIndependent");

  const existingFirm = useMemo(() => {
    if (Array.isArray(firmData)) {
      return firmData.length ? firmData[0] : null;
    }

    if (firmData && typeof firmData === "object") {
      return firmData;
    }

    const fallbackFirm = LoginUserData?.law_firm || LoginUserData?.firm || null;

    return fallbackFirm || null;
  }, [firmData, LoginUserData]);

  // Initialize form with user data
  useEffect(() => {
    if (LoginUserData) {
      let parsedMobile = LoginUserData.mobile || "";
      if (parsedMobile?.startsWith("+")) {
        const [code, ...rest] = parsedMobile.split(" ");
        if (code) {
          setSelectedCountry(code);
        }
        parsedMobile = rest.join("").trim();
      }

      // Parse extra data if it exists
      const extraData = LoginUserData.extra || {};

      const initialValues = {
        first_name: LoginUserData.first_name || "",
        last_name: LoginUserData.last_name || "",
        mobile: parsedMobile,
        address_1: LoginUserData.address_1 || "",
        city: LoginUserData.city || "",
        state: LoginUserData.state || "",
        country: LoginUserData.country || "",
        pincode: LoginUserData.pincode || "",
        birth_date: LoginUserData.birth_date
          ? dayjs(LoginUserData.birth_date)
          : null,
        gender: LoginUserData.gender || "",
        avatar: null,

        // Set values from extra data
        experience: extraData.experience || "",
        licenseNumber: extraData.licenseNumber || "",
        specialization: extraData.specialization || "",
        consultationFee: extraData.consultationFee || "",
        lawFirmOrIndependent: extraData.lawFirmOrIndependent || "",
        previousLawFirm: extraData.previousLawFirm || "",
        positionHeld: extraData.positionHeld || "",
        barCouncilRegistrationNumber:
          extraData.barCouncilRegistrationNumber || "",
        yearsOfExperience: extraData.yearsOfExperience || "",
        barLicenseStatus: extraData.barLicenseStatus || "",
        jurisdiction: extraData.jurisdiction || "",
        practice_area: extraData.practice_area || [],
        courtAffiliations: extraData.courtAffiliations || [],
        typesOfCasesHandled: extraData.typesOfCasesHandled || "",
        location: extraData.location || "",
        languages: extraData.languages || "",
        salary: extraData.salary || "",
        billingPreference: extraData.billingPreference || "",
        employmentType: extraData.employmentType || "",
        courtAppearances: extraData.courtAppearances || false,
        travelWillingness: extraData.travelWillingness || false,
        education: extraData.education?.length
          ? extraData.education.map((edu) => ({
              universityName: edu.universityName || "",
              lawDegree: edu.lawDegree || "",
              graduationYear: edu.graduationYear || "",
              additionalCertifications: edu.additionalCertifications || [],
            }))
          : [
              {
                universityName: "",
                lawDegree: "",
                graduationYear: "",
                additionalCertifications: [],
              },
            ],
      };

      reset(initialValues);

      if (LoginUserData?.avatar) {
        setProfileImage(`${backend_url}${LoginUserData.avatar}`);
      }
    }
  }, [LoginUserData, reset]);

  const handleCountryChange = (newCode) => {
    setSelectedCountry(newCode);
  };

  const handleFileChange = (file) => {
    if (!file) return;

    const fileSizeKB = file.size / 1024;
    const fileSizeMB = fileSizeKB / 1024;

    if (fileSizeMB > MAX_FILE_SIZE_MB) {
      toast.error(
        `File size too large! Maximum allowed size is ${
          MAX_FILE_SIZE_MB * 1024
        } KB. Uploaded file size: ${Math.round(fileSizeKB)} KB.`,
      );
      return;
    }

    if (FILE_TYPES.includes(file.type.split("/")[1]?.toUpperCase())) {
      setIsImageLoading(true);
      const previewUrl = URL.createObjectURL(file);
      setProfileImage(previewUrl);
      setValue("avatar", file, { shouldValidate: true, shouldDirty: true });

      const reader = new FileReader();
      reader.onload = (e) => {
        const base64String = e.target.result;
        sessionStorage.setItem("avatarPreview", base64String);
        URL.revokeObjectURL(previewUrl);
        setProfileImage(base64String);
        setIsImageLoading(false);
      };
      reader.onerror = () => {
        setIsImageLoading(false);
        toast.error("Failed to load image preview. Please try another image.");
      };
      reader.readAsDataURL(file);
    } else {
      toast.error("Invalid file type! Please upload a valid image file.");
    }
  };

  const handleDeleteImage = () => {
    setProfileImage(null);
    setIsImageLoading(false);
    sessionStorage.removeItem("avatarPreview");
    setValue("avatar", null, { shouldValidate: true, shouldDirty: true });
  };

  const handleNext = async () => {
    if (activeStep < steps.length - 1) {
      setActiveStep((prev) => prev + 1);
    } else {
      await handleSubmit(onSubmit)();
    }
  };

  const onSubmit = async (data) => {
    if (!user?.id) {
      toast.error("Lawyer ID not found. Please log in again.");
      return;
    }

    setIsSubmitting(true);
    try {
      const formData = new FormData();

      // Add basic profile fields directly
      formData.append("first_name", data.first_name);
      formData.append("last_name", data.last_name);
      formData.append("gender", data.gender);
      formData.append("address_1", data.address_1);
      formData.append("city", data.city);
      formData.append("state", data.state);
      formData.append("country", data.country);
      formData.append("pincode", data.pincode);

      if (data.birth_date) {
        formData.append(
          "birth_date",
          dayjs(data.birth_date).format("YYYY-MM-DD"),
        );
      }

      const mobileWithCode = `${selectedCountry} ${data.mobile}`;
      formData.append("mobile", mobileWithCode);

      if (data.avatar instanceof File) {
        formData.append("avatar", data.avatar);
      }

      // Build extra object from all professional fields
      const updatedExtra = {
        experience: data.experience,
        licenseNumber: data.licenseNumber,
        specialization: data.specialization,
        consultationFee: data.consultationFee,
        education: data.education,
        lawFirmOrIndependent: data.lawFirmOrIndependent,
        previousLawFirm: data.previousLawFirm,
        positionHeld: data.positionHeld,
        barCouncilRegistrationNumber: data.barCouncilRegistrationNumber,
        yearsOfExperience: data.yearsOfExperience,
        barLicenseStatus: data.barLicenseStatus,
        jurisdiction: data.jurisdiction,
        practice_area: data.practice_area,
        courtAffiliations: data.courtAffiliations,
        typesOfCasesHandled: data.typesOfCasesHandled,
        location: data.location,
        languages: data.languages,
        salary: data.salary,
        billingPreference: data.billingPreference,
        employmentType: data.employmentType,
        courtAppearances: data.courtAppearances,
        travelWillingness: data.travelWillingness,
      };

      // If there are existing extra fields not in the form, merge them
      // (optional, based on your backend requirements)
      if (user?.extra) {
        Object.assign(updatedExtra, user.extra);
      }

      formData.append("extra", JSON.stringify(updatedExtra));

      // FIXED: correct endpoint URL (lawyer instead of lawer)
      const response = await axios.put(
        `${backend_api}lawyer/update-profile-lawyer/${user?.id}/`,
        formData,
        {
          headers: {
            Authorization: `Token ${token}`,
            "Content-Type": "multipart/form-data",
          },
        },
      );

      if (response.status === 200) {
        toast.success("Profile updated successfully!");

        // Create a law firm if none exists
        if (!existingFirm) {
          try {
            await axios.post(
              `${backend_api}firm/create-law-firm/`,
              {
                name: `${data.first_name} ${data.last_name}`,
              },
              {
                headers: {
                  Authorization: `Token ${token}`,
                  "Content-Type": "application/json",
                },
              },
            );
            toast.success("Your firm has been activated.");
          } catch (firmError) {
            console.error("Failed to create firm:", firmError);
            // Non-blocking error, don't stop success message
          }
        }

        closeDialog();
      }
    } catch (err) {
      console.error("Profile update error:", err);
      toast.error(err.response?.data?.message || "Failed to update profile");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBack = () => {
    if (activeStep > 0) {
      setActiveStep((prev) => prev - 1);
    }
  };

  // Memoized practice area options for better performance
  const practiceAreaOptions = useMemo(() => {
    return allPracticeArea || [];
  }, [allPracticeArea]);

  return (
    <Box
      sx={{
        width: "100%",
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        gap: "1rem",
      }}
    >
      {/* Progress Bar */}
      <Box sx={{ width: "100%", mb: 2 }}>
        <LinearProgress
          variant="determinate"
          value={progress}
          sx={{ borderRadius: "1rem", height: 8 }}
        />
        <Typography
          variant="caption"
          sx={{ mt: 0.5, display: "block", textAlign: "center" }}
        >
          Step {activeStep + 1} of {steps.length} - {steps[activeStep].label}
        </Typography>
      </Box>

      {/* Form Box */}
      <Box
        sx={{
          width: "100%",
          p: "1rem",
          borderRadius: "1rem",
          border: "1px solid #e5e5e5",
          backgroundColor: "background.paper",
        }}
      >
        <form onSubmit={handleSubmit(onSubmit)}>
          {activeStep === 0 && (
            <Box sx={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
              <Box
                display="flex"
                gap="1rem"
                flexDirection={{ xs: "column", sm: "row" }}
              >
                {/* Avatar Upload Section */}
                <Controller
                  name="avatar"
                  control={control}
                  render={({ field }) => (
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: "1rem",
                      }}
                    >
                      {profileImage ? (
                        <Box
                          sx={{
                            position: "relative",
                            width: "7rem",
                            height: "6rem",
                            borderRadius: 2,
                            overflow: "hidden",
                            border: "1px dashed #dadada",
                          }}
                        >
                          <img
                            src={profileImage}
                            alt="Profile"
                            style={{
                              width: "100%",
                              height: "100%",
                              objectFit: "cover",
                            }}
                            onLoad={() => setIsImageLoading(false)}
                          />
                          {isImageLoading && (
                            <Box
                              sx={{
                                position: "absolute",
                                top: 0,
                                left: 0,
                                width: "100%",
                                height: "100%",
                                bgcolor: "rgba(255,255,255,0.8)",
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "center",
                              }}
                            >
                              <CircularProgress size={24} />
                            </Box>
                          )}
                          <Box
                            sx={{
                              position: "absolute",
                              top: 0,
                              left: 0,
                              width: "100%",
                              height: "100%",
                              bgcolor: "rgba(0,0,0,0)",
                              display: "flex",
                              justifyContent: "center",
                              alignItems: "center",
                              transition: "background-color 0.3s",
                              "&:hover": {
                                bgcolor: "rgba(0,0,0,0.5)",
                                ".deleteBtn": { display: "flex" },
                              },
                            }}
                          >
                            <IconButton
                              onClick={handleDeleteImage}
                              className="deleteBtn"
                              sx={{
                                display: "none",
                                "&:hover": { color: "red" },
                                position: "absolute",
                                top: "50%",
                                left: "50%",
                                transform: "translate(-50%, -50%)",
                                zIndex: 10,
                              }}
                            >
                              <CloseRounded />
                            </IconButton>
                          </Box>
                        </Box>
                      ) : (
                        <FileUploader
                          handleChange={(file) => {
                            handleFileChange(file);
                            field.onChange(file);
                          }}
                          name="avatar"
                          types={FILE_TYPES}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              flexDirection: "column",
                              justifyContent: "center",
                              alignItems: "center",
                              border: "1px dashed #dadada",
                              p: "1rem",
                              borderRadius: "1rem",
                              width: "7rem",
                              height: "6rem",
                              textAlign: "center",
                            }}
                          >
                            <AddPhotoAlternateRoundedIcon
                              sx={{
                                fontSize: "1.5rem",
                                color: "#6D6E6F",
                              }}
                            />
                            <Typography
                              variant="body2"
                              color={
                                errors?.avatar ? "error" : "text.secondary"
                              }
                              sx={{ fontSize: "0.7rem" }}
                            >
                              {errors?.avatar
                                ? "Picture is required"
                                : "Upload Profile Picture"}
                            </Typography>
                          </Box>
                        </FileUploader>
                      )}
                    </Box>
                  )}
                />

                {/* Personal Information Fields */}
                <Box
                  sx={{
                    flex: 1,
                    display: "flex",
                    flexDirection: "column",
                    gap: "1rem",
                  }}
                >
                  {/* Name Fields */}
                  <Box
                    sx={{
                      display: "flex",
                      gap: "1rem",
                      flexDirection: { xs: "column", sm: "row" },
                    }}
                  >
                    <Controller
                      name="first_name"
                      control={control}
                      rules={{ required: "First name is required" }}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          label="First Name"
                          size="small"
                          fullWidth
                          error={!!errors.first_name}
                          helperText={errors.first_name?.message}
                        />
                      )}
                    />
                    <Controller
                      name="last_name"
                      control={control}
                      rules={{ required: "Last name is required" }}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          label="Last Name"
                          size="small"
                          fullWidth
                          error={!!errors.last_name}
                          helperText={errors.last_name?.message}
                        />
                      )}
                    />
                  </Box>

                  {/* Date and Gender Fields */}
                  <Box
                    sx={{
                      display: "flex",
                      gap: "1rem",
                      flexDirection: { xs: "column", sm: "row" },
                    }}
                  >
                    <Box sx={{ width: "100%" }}>
                      <Controller
                        name="birth_date"
                        control={control}
                        render={({ field }) => (
                          <DatePicker
                            value={field.value}
                            onChange={(newValue) => field.onChange(newValue)}
                            slotProps={{
                              textField: {
                                label: "Date of Birth",
                                size: "small",
                                fullWidth: true,
                                error: !!errors.birth_date,
                                helperText: errors.birth_date?.message,
                              },
                            }}
                            format="YYYY-MM-DD"
                            maxDate={dayjs()}
                          />
                        )}
                      />
                    </Box>

                    <Controller
                      name="gender"
                      control={control}
                      rules={{ required: "Gender is required" }}
                      render={({ field }) => (
                        <FormControl
                          size="small"
                          fullWidth
                          error={!!errors.gender}
                        >
                          <InputLabel id="gender-label">Gender</InputLabel>
                          <Select
                            {...field}
                            labelId="gender-label"
                            label="Gender"
                          >
                            {genders?.map((item, i) => (
                              <MenuItem key={i} value={item}>
                                {item}
                              </MenuItem>
                            ))}
                          </Select>
                          {errors.gender && (
                            <FormHelperText>
                              {errors.gender.message}
                            </FormHelperText>
                          )}
                        </FormControl>
                      )}
                    />
                  </Box>

                  {/* Mobile Number Field */}
                  <Box display="flex" alignItems="flex-start" width="100%">
                    <CountryCodeSelect
                      value={selectedCountry}
                      onChange={handleCountryChange}
                    />
                    <Controller
                      name="mobile"
                      control={control}
                      rules={{
                        required: "Mobile number is required",
                        pattern: {
                          value: /^[0-9]{10}$/,
                          message:
                            "Please enter a valid 10-digit mobile number",
                        },
                      }}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          size="small"
                          type="tel"
                          fullWidth
                          label="Mobile Number"
                          error={!!errors.mobile}
                          helperText={errors.mobile?.message}
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              borderTopLeftRadius: 0,
                              borderBottomLeftRadius: 0,
                            },
                          }}
                        />
                      )}
                    />
                  </Box>
                </Box>
              </Box>

              <Controller
                name="address_1"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    size="small"
                    label="Full Address"
                    placeholder="Enter the full address"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <HomeRoundedIcon sx={{ color: "#DEDEDE" }} />
                        </InputAdornment>
                      ),
                    }}
                  />
                )}
              />

              <Box
                sx={{ display: "flex", flexDirection: "column", gap: "1rem" }}
              >
                <Box
                  sx={{
                    display: "flex",
                    gap: "1rem",
                    flexDirection: { xs: "column", sm: "row" },
                  }}
                >
                  <Controller
                    name="city"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="City"
                        size="small"
                        fullWidth
                      />
                    )}
                  />
                  <Controller
                    name="state"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="State"
                        size="small"
                        fullWidth
                      />
                    )}
                  />
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    gap: "1rem",
                    flexDirection: { xs: "column", sm: "row" },
                  }}
                >
                  <Controller
                    name="pincode"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="Pin Code"
                        size="small"
                        fullWidth
                      />
                    )}
                  />
                  <Controller
                    name="country"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="Country"
                        size="small"
                        fullWidth
                      />
                    )}
                  />
                </Box>
              </Box>
            </Box>
          )}

          {activeStep === 1 && (
            <Box sx={{ display: "flex", flexDirection: "column", gap: "2rem" }}>
              {fields.map((field, index) => (
                <Box
                  key={field.id}
                  sx={{
                    p: 2,
                    border: "1px solid #e0e0e0",
                    borderRadius: "8px",
                    position: "relative",
                    backgroundColor: "background.paper",
                  }}
                >
                  {index > 0 && (
                    <IconButton
                      onClick={() => remove(index)}
                      sx={{
                        position: "absolute",
                        top: 8,
                        right: 8,
                        color: "error.main",
                      }}
                    >
                      <DeleteRoundedIcon fontSize="small" />
                    </IconButton>
                  )}

                  <Typography
                    variant="subtitle1"
                    sx={{ mb: 2, fontWeight: 500 }}
                  >
                    Education #{index + 1}
                  </Typography>

                  <Box
                    sx={{
                      display: "flex",
                      gap: "1rem",
                      mb: 2,
                      flexDirection: { xs: "column", md: "row" },
                    }}
                  >
                    <Controller
                      name={`education.${index}.universityName`}
                      control={control}
                      rules={{ required: "University name is required" }}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          size="small"
                          label="University Name"
                          fullWidth
                          error={!!errors.education?.[index]?.universityName}
                          helperText={
                            errors.education?.[index]?.universityName?.message
                          }
                        />
                      )}
                    />
                    <Controller
                      name={`education.${index}.lawDegree`}
                      control={control}
                      rules={{ required: "Law degree is required" }}
                      render={({ field }) => (
                        <FormControl
                          fullWidth
                          size="small"
                          error={!!errors.education?.[index]?.lawDegree}
                        >
                          <Autocomplete
                            options={degreeOptions}
                            getOptionLabel={(option) => option.name}
                            value={
                              degreeOptions.find(
                                (opt) => opt.id === field.value,
                              ) || null
                            }
                            onChange={(_, newValue) =>
                              field.onChange(newValue ? newValue.id : "")
                            }
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                label="Law Degree"
                                size="small"
                                error={!!errors.education?.[index]?.lawDegree}
                                helperText={
                                  errors.education?.[index]?.lawDegree?.message
                                }
                              />
                            )}
                          />
                        </FormControl>
                      )}
                    />
                  </Box>

                  <Controller
                    name={`education.${index}.graduationYear`}
                    control={control}
                    rules={{
                      required: "Graduation year is required",
                      pattern: {
                        value: /^(19|20)\d{2}$/,
                        message: "Enter a valid year (1900-2099)",
                      },
                    }}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        size="small"
                        label="Graduation Year"
                        fullWidth
                        type="number"
                        sx={{ mb: 2 }}
                        error={!!errors.education?.[index]?.graduationYear}
                        helperText={
                          errors.education?.[index]?.graduationYear?.message
                        }
                      />
                    )}
                  />

                  <Controller
                    name={`education.${index}.additionalCertifications`}
                    control={control}
                    render={({ field }) => (
                      <FormControl fullWidth size="small">
                        <Autocomplete
                          size="small"
                          multiple
                          options={certificationOptions}
                          getOptionLabel={(option) => option.name}
                          value={field.value
                            .map((id) =>
                              certificationOptions.find(
                                (cert) => cert.id === id,
                              ),
                            )
                            .filter(Boolean)}
                          onChange={(_, newValue) =>
                            field.onChange(newValue.map((item) => item.id))
                          }
                          renderTags={(tagValues, getTagProps) => (
                            <Box
                              sx={{
                                display: "flex",
                                flexWrap: "wrap",
                                gap: 0.5,
                              }}
                            >
                              {tagValues.map((option, index) => (
                                <Chip
                                  size="small"
                                  key={option.id}
                                  label={option.name}
                                  {...getTagProps({ index })}
                                />
                              ))}
                            </Box>
                          )}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label="Additional Certifications"
                              placeholder="Select certifications"
                            />
                          )}
                        />
                      </FormControl>
                    )}
                  />
                </Box>
              ))}

              <Button
                variant="outlined"
                startIcon={<AddCircleOutlineRounded />}
                onClick={() =>
                  append({
                    universityName: "",
                    lawDegree: "",
                    graduationYear: "",
                    additionalCertifications: [],
                  })
                }
                sx={{ alignSelf: "flex-start" }}
              >
                Add Another Education
              </Button>
            </Box>
          )}

          {activeStep === 2 && (
            <Box sx={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
              <Controller
                name="lawFirmOrIndependent"
                control={control}
                defaultValue=""
                render={({ field, fieldState }) => (
                  <FormControl component="fieldset">
                    <FormLabel component="legend">
                      Law Firm/Independent Practice
                    </FormLabel>
                    <RadioGroup
                      {...field}
                      aria-label="lawFirmOrIndependent"
                      name="lawFirmOrIndependent"
                      value={field.value}
                      onChange={field.onChange}
                      sx={{
                        display: "flex",
                        flexDirection: "row",
                        flexWrap: "wrap",
                      }}
                    >
                      <FormControlLabel
                        value="lawFirm"
                        control={<Radio />}
                        label="Law Firm"
                        sx={{ mr: 2 }}
                      />
                      <FormControlLabel
                        value="independent"
                        control={<Radio />}
                        label="Independent Practice"
                      />
                    </RadioGroup>
                  </FormControl>
                )}
              />

              {lawFirmOrIndependent === "lawFirm" && (
                <Box
                  sx={{
                    display: "flex",
                    gap: "1rem",
                    flexDirection: { xs: "column", sm: "row" },
                  }}
                >
                  <Controller
                    name="previousLawFirm"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        size="small"
                        label="Previous Law Firms"
                        fullWidth
                      />
                    )}
                  />
                  <Controller
                    name="positionHeld"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        size="small"
                        label="Position Held"
                        fullWidth
                      />
                    )}
                  />
                </Box>
              )}

              <Box
                sx={{
                  display: "flex",
                  gap: "1rem",
                  flexDirection: { xs: "column", sm: "row" },
                }}
              >
                <Controller
                  name="barCouncilRegistrationNumber"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      size="small"
                      label="Bar Council Registration Number"
                      fullWidth
                    />
                  )}
                />
                <Controller
                  name="yearsOfExperience"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      size="small"
                      label="Years of Experience"
                      type="number"
                      fullWidth
                    />
                  )}
                />
              </Box>

              <Box
                sx={{
                  display: "flex",
                  gap: "1rem",
                  flexDirection: { xs: "column", sm: "row" },
                }}
              >
                <Controller
                  name="barLicenseStatus"
                  control={control}
                  render={({ field }) => (
                    <FormControl size="small" fullWidth>
                      <InputLabel id="barLicenseStatus-label">
                        Bar License Status
                      </InputLabel>
                      <Select
                        {...field}
                        labelId="barLicenseStatus-label"
                        label="Bar License Status"
                      >
                        <MenuItem value="licensed">Licensed</MenuItem>
                        <MenuItem value="pending">Pending</MenuItem>
                        <MenuItem value="not-licensed">Not Licensed</MenuItem>
                      </Select>
                    </FormControl>
                  )}
                />
                <Controller
                  name="licenseNumber"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      size="small"
                      label="License Number"
                      fullWidth
                    />
                  )}
                />
              </Box>

              <Box
                sx={{ display: "flex", flexDirection: "column", gap: "1rem" }}
              >
                <Controller
                  name="jurisdiction"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Jurisdiction"
                      fullWidth
                      size="small"
                    />
                  )}
                />

                <Controller
                  name="practice_area"
                  control={control}
                  render={({ field }) => (
                    <FormControl size="small" fullWidth>
                      <Autocomplete
                        multiple
                        size="small"
                        options={practiceAreaOptions}
                        getOptionLabel={(option) => option?.name || ""}
                        value={practiceAreaOptions.filter((option) =>
                          field.value.includes(option.id),
                        )}
                        onChange={(_, newValue) => {
                          field.onChange(newValue.map((item) => item.id));
                        }}
                        isOptionEqualToValue={(option, value) =>
                          option.id === value.id
                        }
                        renderTags={(tagValues, getTagProps) => (
                          <Box
                            sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}
                          >
                            {tagValues?.map((option, index) => (
                              <Chip
                                size="small"
                                key={option.id}
                                label={option.name}
                                {...getTagProps({ index })}
                              />
                            ))}
                          </Box>
                        )}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Best Practice Areas"
                            placeholder="Select practice areas"
                          />
                        )}
                      />
                    </FormControl>
                  )}
                />

                <Controller
                  name="courtAffiliations"
                  control={control}
                  render={({ field }) => (
                    <FormControl fullWidth>
                      <Autocomplete
                        multiple
                        options={courtOptions}
                        getOptionLabel={(option) => option.name}
                        value={
                          field.value.map((id) =>
                            courtOptions.find((court) => court.id === id),
                          ) || []
                        }
                        onChange={(_, newValue) =>
                          field.onChange(newValue.map((item) => item.id))
                        }
                        isOptionEqualToValue={(option, value) =>
                          option.id === value.id
                        }
                        renderTags={(selected, getTagProps) =>
                          selected.map((option, index) => (
                            <Chip
                              size="small"
                              key={option.id}
                              label={option.name}
                              {...getTagProps({ index })}
                            />
                          ))
                        }
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            size="small"
                            label="Court Appearance"
                            fullWidth
                          />
                        )}
                      />
                    </FormControl>
                  )}
                />

                <Controller
                  name="typesOfCasesHandled"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Types of Cases Handled"
                      fullWidth
                      multiline
                      rows={3}
                    />
                  )}
                />
              </Box>
            </Box>
          )}

          {activeStep === 3 && (
            <Box sx={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
              <Box
                sx={{
                  display: "flex",
                  gap: "1rem",
                  flexDirection: { xs: "column", sm: "row" },
                }}
              >
                <Controller
                  name="location"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Preference Location"
                      fullWidth
                      size="small"
                      helperText="Comma separated values (e.g., New York, London)"
                    />
                  )}
                />
                <Controller
                  name="languages"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Languages Spoken"
                      fullWidth
                      size="small"
                      helperText="Comma separated values (e.g., English, Spanish)"
                    />
                  )}
                />
              </Box>

              <Box
                sx={{
                  display: "flex",
                  gap: "1rem",
                  flexDirection: { xs: "column", sm: "row" },
                }}
              >
                <Controller
                  name="salary"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Expected Salary ($)"
                      type="number"
                      fullWidth
                      size="small"
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">$</InputAdornment>
                        ),
                      }}
                    />
                  )}
                />
                <Controller
                  name="billingPreference"
                  control={control}
                  render={({ field }) => (
                    <FormControl size="small" fullWidth>
                      <InputLabel id="billingPreference-label">
                        Billing Preference
                      </InputLabel>
                      <Select
                        {...field}
                        labelId="billingPreference-label"
                        label="Billing Preference"
                      >
                        <MenuItem value="hourly">Hourly</MenuItem>
                        <MenuItem value="fixed">Fixed</MenuItem>
                        <MenuItem value="contingency">Contingency</MenuItem>
                      </Select>
                    </FormControl>
                  )}
                />
              </Box>

              <Controller
                name="employmentType"
                control={control}
                render={({ field }) => (
                  <FormControl size="small" fullWidth>
                    <InputLabel id="employmentType-label">
                      Employment Type & Work Preference
                    </InputLabel>
                    <Select
                      {...field}
                      labelId="employmentType-label"
                      label="Employment Type & Work Preference"
                    >
                      <MenuItem value="full-time-remote">
                        Full-Time (Remote)
                      </MenuItem>
                      <MenuItem value="full-time-hybrid">
                        Full-Time (Hybrid)
                      </MenuItem>
                      <MenuItem value="full-time-in-person">
                        Full-Time (In-Person)
                      </MenuItem>
                      <MenuItem value="freelance-remote">
                        Freelance (Remote)
                      </MenuItem>
                      <MenuItem value="freelance-hybrid">
                        Freelance (Hybrid)
                      </MenuItem>
                      <MenuItem value="freelance-in-person">
                        Freelance (In-Person)
                      </MenuItem>
                      <MenuItem value="remote-only">Remote Only</MenuItem>
                    </Select>
                  </FormControl>
                )}
              />

              <Box sx={{ display: "flex", gap: "1rem", flexWrap: "wrap" }}>
                <Controller
                  name="courtAppearances"
                  control={control}
                  render={({ field }) => (
                    <FormControlLabel
                      control={<Checkbox {...field} checked={field.value} />}
                      label="Available for Court Appearances"
                    />
                  )}
                />

                <Controller
                  name="travelWillingness"
                  control={control}
                  render={({ field }) => (
                    <FormControlLabel
                      control={<Checkbox {...field} checked={field.value} />}
                      label="Willing to Travel for Cases"
                    />
                  )}
                />
              </Box>
            </Box>
          )}
        </form>
      </Box>

      {/* Stepper & Navigation */}
      <Box sx={{ width: "100%", mt: 2 }}>
        <MobileStepper
          variant="dots"
          steps={steps.length}
          position="static"
          activeStep={activeStep}
          sx={{
            flexGrow: 1,
            bgcolor: "transparent",
            "& .MuiMobileStepper-dot": {
              width: 8,
              height: 8,
            },
          }}
          nextButton={
            <Button
              size="small"
              onClick={handleNext}
              disabled={isSubmitting}
              endIcon={
                activeStep === steps.length - 1 ? null : theme.direction ===
                  "rtl" ? (
                  <KeyboardArrowLeftRoundedIcon />
                ) : (
                  <KeyboardArrowRightRoundedIcon />
                )
              }
              variant="contained"
              sx={{ minWidth: 100 }}
            >
              {isSubmitting ? (
                <CircularProgress size={20} color="inherit" />
              ) : activeStep === steps.length - 1 ? (
                "Submit"
              ) : (
                "Next"
              )}
            </Button>
          }
          backButton={
            <Button
              size="small"
              onClick={handleBack}
              disabled={activeStep === 0 || isSubmitting}
              startIcon={
                theme.direction === "rtl" ? (
                  <KeyboardArrowRightRoundedIcon />
                ) : (
                  <KeyboardArrowLeftRoundedIcon />
                )
              }
              sx={{ minWidth: 100 }}
            >
              Back
            </Button>
          }
        />
      </Box>
    </Box>
  );
};

export default LawyerProfileForm;
